import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArchiveRestore, Archive, Briefcase, Activity } from 'lucide-react';
import { clientApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';

// Founder small improvement batch (2026-09-05) — governed archive/history
// center. Read-only view of archived operational records; restore is offered
// ONLY where existing Client Admin governance already allows it (TalentStart
// onboardings). No hard delete exists anywhere on this page.
const KIND_META = {
  onboarding: { icon: Briefcase, labelKey: 'clientArchive.kindOnboarding' },
  climara_campaign: { icon: Activity, labelKey: 'clientArchive.kindClimara' },
};

export default function ClientArchiveCenter() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    clientApi.archiveItems()
      .then((d) => setItems(d.items || []))
      .catch((e) => toast.error(t('toasts.loadFailed'), { description: formatApiError(e) }))
      .finally(() => setLoading(false));
  }, [t]);
  useEffect(load, [load]);

  const restore = async (item) => {
    if (!window.confirm(t('clientArchive.restoreConfirm'))) return;
    setBusy(item.id);
    try {
      await clientApi.onboardingRestoreAssignment(item.id);
      toast.success(t('clientArchive.restored'));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(null);
  };

  const fmtDate = (iso) => (iso
    ? new Date(iso).toLocaleDateString(i18n.language?.startsWith('es') ? 'es' : 'en', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—');

  return (
    <>
      <Topbar title={t('clientArchive.title')} subtitle={t('clientArchive.subtitle')} />
      <div className="p-6 lg:p-8" data-testid="client-archive-center">
        <Card className="shadow-card overflow-hidden">
          {loading ? (
            <p className="p-8 text-center text-muted-foreground" data-testid="archive-loading">…</p>
          ) : items.length === 0 ? (
            <div className="p-10 text-center" data-testid="archive-empty">
              <Archive className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
              <p className="text-sm text-muted-foreground">{t('clientArchive.empty')}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('clientArchive.colType')}</TableHead>
                  <TableHead>{t('clientArchive.colName')}</TableHead>
                  <TableHead>{t('clientArchive.colStatus')}</TableHead>
                  <TableHead>{t('clientArchive.colArchivedAt')}</TableHead>
                  <TableHead>{t('clientArchive.colActor')}</TableHead>
                  <TableHead className="text-right">{t('clientArchive.colActions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it) => {
                  const meta = KIND_META[it.kind] || KIND_META.onboarding;
                  const Icon = meta.icon;
                  return (
                    <TableRow key={`${it.kind}-${it.id}`} data-testid={`archive-row-${it.id}`}>
                      <TableCell>
                        <span className="inline-flex items-center gap-1.5 text-sm">
                          <Icon className="h-4 w-4 text-muted-foreground" />
                          {t(meta.labelKey)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium text-sm">{it.name}</p>
                        {it.detail && <p className="text-xs text-muted-foreground">{it.detail}</p>}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {t(`clientArchive.status.${it.status}`, { defaultValue: it.status || '—' })}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm tabular-nums">{fmtDate(it.archived_at)}</TableCell>
                      <TableCell className="text-sm">{it.archived_by || '—'}</TableCell>
                      <TableCell className="text-right">
                        {it.restorable ? (
                          <Button size="sm" variant="outline" disabled={busy === it.id}
                                  onClick={() => restore(it)} data-testid={`archive-restore-${it.id}`}>
                            <ArchiveRestore className="h-3.5 w-3.5 mr-1.5" />
                            {t('clientArchive.restore')}
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">{t('clientArchive.readOnly')}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>
        <p className="text-xs text-muted-foreground mt-3" data-testid="archive-governance-note">
          {t('clientArchive.governanceNote')}
        </p>
      </div>
    </>
  );
}
